import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  Typography,
  Box,
  Chip,
  Alert,
  Paper
} from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import { useNotifier } from '../../../../components/Notifier';
import { createTireTransaction } from '../../../../stores/tireStore';
import { uploadTireScrapImage } from '../../../../stores/tireBatchStore';
import IconButton from '@mui/material/IconButton';
import ClearIcon from '@mui/icons-material/Clear';
import Tooltip from '@mui/material/Tooltip';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import PickerFieldTable from '../../../../components/PickerFieldTable';
import usePickerPagination from '../../../../hooks/usePickerPagination';
import { useWarehouseDataList } from '../../../../stores/warehouseStore';
import { useLanguage } from '../../../../utils/i18n';

export default function TabTireScrap({ open, onClose, selectedTires = [], onSuccess }) {
  const { t } = useLanguage();
  const isBulk = selectedTires.length > 1;
  const singleSn = !isBulk && selectedTires.length === 1 ? selectedTires[0].serial_number : '';

  const notify = useNotifier();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const getCompany = () => {
    try {
      const rawUser = localStorage.getItem('user');
      if (rawUser) {
        const parsed = JSON.parse(rawUser);
        if (parsed && (parsed.company || parsed.company_id)) {
          return parsed.company || parsed.company_id;
        }
      }
      return localStorage.getItem('companyId') || 1;
    } catch {
      return 1;
    }
  };

  const companyId = getCompany();

  const [scrapFiles, setScrapFiles] = useState({});
  const [scrapFilePreviews, setScrapFilePreviews] = useState({});

  const [formData, setFormData] = useState({
    scrapReason: '',
    finalRtd: '',
    mechanicName: '',
    notes: '',
    confirmationText: '',
    kmUnit: '',
    hmUnit: '',
    transactionDate: dayjs(),
    warehouseId: null,
    warehouseName: ''
  });

  const warehousePickerParams = usePickerPagination({
    fetchHook: useWarehouseDataList,
    buildArgs: (state) => [
      state.page,
      state.pageSize,
      companyId ? [{ value: companyId }] : [],
      state.searchKey,
      state.searchValue,
      [{ field: 'warehouse_type', operator: '=', value: 'scrap-yard' }]
    ],
    searchKey: 'name',
    formatOption: (item) => ({
      ...item,
      value: String(item.id),
      label: item.name || String(item.id)
    })
  });

  useEffect(() => {
    if (open) {
      setFormData({
        scrapReason: '',
        finalRtd: '',
        mechanicName: '',
        notes: '',
        confirmationText: '',
        kmUnit: '',
        hmUnit: '',
        transactionDate: dayjs(),
        warehouseId: null,
        warehouseName: ''
      });
      setScrapFiles({});
      setScrapFilePreviews({});
      selectedTires.forEach((t) => {
        const inputElement = document.getElementById(`scrap-proof-upload-${t.id}`);
        if (inputElement) inputElement.value = '';
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e, tireId) => {
    const file = e.target.files?.[0];
    if (file) {
      setScrapFiles((prev) => ({ ...prev, [tireId]: file }));
      setScrapFilePreviews((prev) => ({ ...prev, [tireId]: URL.createObjectURL(file) }));
    }
  };

  const handleClearFile = (e, tireId) => {
    e.stopPropagation();
    e.preventDefault();
    setScrapFiles((prev) => {
      const next = { ...prev };
      delete next[tireId];
      return next;
    });
    setScrapFilePreviews((prev) => {
      const next = { ...prev };
      delete next[tireId];
      return next;
    });
    const inputElement = document.getElementById(`scrap-proof-upload-${tireId}`);
    if (inputElement) inputElement.value = '';
  };

  const isConfirmed = isBulk ? formData.confirmationText === 'CONFIRM' : formData.confirmationText === singleSn;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isConfirmed || !selectedTires.length) return;

    setIsSubmitting(true);
    let successfulCount = 0;

    for (const tire of selectedTires) {
      const payload = {
        company: companyId,
        current_odo: 0,
        km_unit: formData.kmUnit ? parseInt(formData.kmUnit) : 0,
        hm_unit: formData.hmUnit ? parseInt(formData.hmUnit) : 0,
        transaction_date: formData.transactionDate ? dayjs(formData.transactionDate).format('YYYY-MM-DD') : '',
        last_position: tire.last_position || tire.posCode || '',
        reason: formData.scrapReason,
        notes: formData.notes,
        serial_number: tire.serial_number,
        tire_item: tire.tire_item || tire.tire_item_id || tire.id,
        transaction_type: 'SCRAP',
        tire_damage: tire.last_inspection?.tire_damage_id || tire.tire_damage_id || 1,
        final_rtd: parseInt(formData.finalRtd) || 0,
        tire_damage_name: tire.last_inspection?.tire_damage_name || tire.tire_damage_name || 'UNKNOWN',
        mechanic_name: formData.mechanicName,
        condition_after: 'DAMAGED'
      };

      try {
        await createTireTransaction(payload);

        // Upload photo proof if provided
        if (scrapFiles[tire.id]) {
          try {
            await uploadTireScrapImage(payload.tire_item, scrapFiles[tire.id]);
          } catch {
            notify({
              message: `${t('tireRepairScrap.scrapSuccessFailedPhoto', 'Scrap success but failed to upload photo')} (${tire.serial_number})`,
              severity: 'warning'
            });
          }
        }

        successfulCount++;
      } catch (error) {
        let errorMsg = error.response?.data?.message || error.message || 'Unknown error';
        if (typeof errorMsg !== 'string') {
          errorMsg = JSON.stringify(errorMsg);
        }
        notify({
          message: `${t('tireRepairScrap.failedToScrap', 'Failed to scrap')} ${tire.serial_number}: ${errorMsg}`,
          severity: 'error'
        });
        setIsSubmitting(false);
        // Break early on failure to allow user to correct input
        return;
      }
    }

    if (successfulCount > 0) {
      notify({
        message: `${t('tireRepairScrap.successScrap', 'Successfully requested scrap for')} ${successfulCount} ${t('tireCycle.tires', 'tires')}`,
        severity: 'success'
      });
      if (onSuccess) {
        onSuccess();
      }
    }

    setIsSubmitting(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 'bold', color: 'error.main' }}>
        {t('tireRepairScrap.permanentScrapConfirmation', 'Permanent Scrap Confirmation')}
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent dividers sx={{ bgcolor: 'grey.50' }}>
          <Paper elevation={0} sx={{ p: 2, mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              {t('tireRepairScrap.selectedTires', 'Selected Tires')} ({selectedTires.length})
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {selectedTires.map((tire) => (
                <Chip
                  key={tire.id}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {tire.serial_number}
                      </Typography>
                      {tire.count_retread > 0 && (
                        <Typography
                          variant="caption"
                          sx={{ ml: 0.5, color: tire.count_retread >= 3 ? 'error.main' : 'info.main', fontWeight: 'bold' }}
                        >
                          ({t('tireRepairScrap.retread', 'Retread')}: {tire.count_retread}/3)
                        </Typography>
                      )}
                      <LocalShippingIcon sx={{ fontSize: 16, ml: 0.5, color: 'error.main' }} />
                      <Typography variant="caption">{tire.last_vehicle || '-'}</Typography>
                      <Typography variant="caption" sx={{ ml: 1 }}>
                        {t('detailTireInventory.lastPosition', 'Last Position')}: {tire.last_position || '-'}
                      </Typography>
                    </Box>
                  }
                  color="error"
                  variant="outlined"
                />
              ))}
            </Box>
          </Paper>

          <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
              {t('tireRepairScrap.scrapDetails', 'Scrap Details')}
            </Typography>
            <Grid container spacing={2.5}>
              <Grid item size={{ xs: 12, sm: 6 }}>
                <DatePicker
                  label={t('tireRepairScrap.transactionDate', 'Transaction Date')}
                  format="DD/MM/YYYY"
                  value={formData.transactionDate}
                  onChange={(val) => setFormData((prev) => ({ ...prev, transactionDate: val }))}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true
                    }
                  }}
                />
              </Grid>
              <Grid item size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  required
                  label={t('tireRepairScrap.scrapReason', 'Scrap Reason')}
                  name="scrapReason"
                  value={formData.scrapReason}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  required
                  label={t('tireRepairScrap.mechanicName', 'Mechanic Name')}
                  name="mechanicName"
                  value={formData.mechanicName}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item size={{ xs: 12, sm: 6 }}>
                <PickerFieldTable
                  label={t('tireRepairScrap.sendToScrapYard', 'Send To (Scrap Yard)')}
                  value={formData.warehouseId}
                  onChange={(valId) => {
                    setFormData((prev) => ({ ...prev, warehouseId: valId || null }));
                    if (valId) {
                      const val = warehousePickerParams.options.find((o) => String(o.id) === String(valId));
                      if (val) {
                        setFormData((prev) => ({ ...prev, warehouseName: val.name || '' }));
                      }
                    } else {
                      setFormData((prev) => ({ ...prev, warehouseName: '' }));
                    }
                  }}
                  options={warehousePickerParams.options}
                  loading={warehousePickerParams.loading}
                  onSearch={warehousePickerParams.handleSearch}
                  onScrollEnd={warehousePickerParams.handleScrollEnd}
                  columns={[
                    { field: 'name', headerName: t('general.name', 'Name') },
                    { field: 'warehouse_type', headerName: t('general.type', 'Type') }
                  ]}
                  getOptionId={(o) => o?.value ?? o?.id ?? ''}
                  getOptionLabel={(o) => o?.label ?? o?.name ?? ''}
                  required
                />
              </Grid>
              <Grid item size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  required
                  label={t('tireRepairScrap.finalRtd', 'Final RTD (mm)')}
                  name="finalRtd"
                  type="number"
                  value={formData.finalRtd}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label={t('tireRepairScrap.kmUnit', 'KM Unit')}
                  name="kmUnit"
                  type="number"
                  value={formData.kmUnit}
                  onChange={handleChange}
                  helperText={`${t('tireRepairScrap.lastKm', 'Last KM:')} ${selectedTires.length > 0 ? selectedTires[0].last_km_unit || 0 : 0}`}
                  FormHelperTextProps={{ sx: { color: 'info.main' } }}
                />
              </Grid>
              <Grid item size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label={t('tireRepairScrap.hmUnit', 'HM Unit')}
                  name="hmUnit"
                  type="number"
                  value={formData.hmUnit}
                  onChange={handleChange}
                  helperText={`${t('tireRepairScrap.lastHm', 'Last HM:')} ${selectedTires.length > 0 ? selectedTires[0].last_hm_unit || 0 : 0}`}
                  FormHelperTextProps={{ sx: { color: 'info.main' } }}
                />
              </Grid>
              <Grid item size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  label={t('tireRepairScrap.notes', 'Notes')}
                  name="notes"
                  multiline
                  minRows={3}
                  value={formData.notes}
                  onChange={handleChange}
                />
              </Grid>
            </Grid>

            <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 3, mb: 1.5 }}>
              {t('tireRepairScrap.proofImagesOptional', 'Proof Images (Optional)')}
            </Typography>
            <Grid container spacing={2}>
              {selectedTires.map((tire) => (
                <Grid item size={{ xs: 12, sm: 6, md: 4 }} key={tire.id}>
                  {!scrapFilePreviews[tire.id] ? (
                    <Box
                      onClick={() => document.getElementById(`scrap-proof-upload-${tire.id}`).click()}
                      sx={{
                        border: '1px dashed',
                        borderColor: 'grey.400',
                        borderRadius: 1,
                        height: '56px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 2,
                        cursor: 'pointer',
                        bgcolor: 'grey.50',
                        transition: 'all 0.2s',
                        '&:hover': { bgcolor: 'grey.100', borderColor: 'primary.main' }
                      }}
                    >
                      <Box sx={{ overflow: 'hidden' }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                          {tire.serial_number}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t('tireRepairScrap.clickToUpload', 'Click to upload')}
                        </Typography>
                      </Box>
                      <UploadFileIcon color="action" />
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        border: '1px solid',
                        borderColor: 'grey.300',
                        borderRadius: 1,
                        height: '56px',
                        display: 'flex',
                        alignItems: 'center',
                        px: 1,
                        gap: 1.5,
                        bgcolor: 'white'
                      }}
                    >
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 1,
                          overflow: 'hidden',
                          border: '1px solid',
                          borderColor: 'divider',
                          flexShrink: 0
                        }}
                      >
                        <img src={scrapFilePreviews[tire.id]} alt="Proof" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </Box>
                      <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                          {tire.serial_number}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}
                        >
                          {scrapFiles[tire.id]?.name || t('tireRepairScrap.image', 'Image')}
                        </Typography>
                      </Box>
                      <Tooltip title={t('tireRepairScrap.removeImage', 'Remove Image')}>
                        <IconButton size="small" color="error" onClick={(e) => handleClearFile(e, tire.id)}>
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                  <input
                    id={`scrap-proof-upload-${tire.id}`}
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(e) => handleFileChange(e, tire.id)}
                  />
                </Grid>
              ))}
            </Grid>
          </Paper>

          <Box sx={{ mt: 3 }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {isBulk
                  ? `${t('tireRepairScrap.confirmBulkScrapPrefix', 'I confirm that these')} ${selectedTires.length} ${t('tireRepairScrap.confirmBulkScrapSuffix', 'tires have been inspected and are not repairable. Their status will be permanently changed to Scrap.')}`
                  : `${t('tireRepairScrap.confirmSingleScrapPrefix', 'I confirm that tire')} ${singleSn} ${t('tireRepairScrap.confirmSingleScrapSuffix', 'has been inspected and is not repairable. Its status will be permanently changed to Scrap.')}`}
              </Typography>
            </Alert>
            <TextField
              fullWidth
              required
              size="small"
              label={
                isBulk
                  ? t('tireRepairScrap.typeConfirmToProceed', 'Type "CONFIRM" to proceed')
                  : `${t('tireRepairScrap.typePrefix', 'Type')} "${singleSn}" ${t('tireRepairScrap.toProceedSuffix', 'to proceed')}`
              }
              name="confirmationText"
              value={formData.confirmationText}
              onChange={handleChange}
              color="error"
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'error.50',
                  '&.Mui-focused': { bgcolor: 'error.50' }
                }
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} variant="outlined" color="inherit" disabled={isSubmitting}>
            {t('general.cancel', 'Cancel')}
          </Button>
          <Button type="submit" variant="contained" color="error" disabled={!isConfirmed || isSubmitting}>
            {isSubmitting ? t('tireRepairScrap.scrapping', 'Scrapping...') : t('tireRepairScrap.scrapPermanently', 'Scrap Permanently')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
